"use client";

import { c as _c } from "react/compiler-runtime";
import { useSectionTiming } from "@/hooks/useSectionTiming";
import { Suspense } from "react";
import { AppProviders } from "@/components/AppProviders";
import { useAuth } from "@/components/AuthProvider";
import { OrgAccessSplash } from "@/components/OrgAccessSplash";
import { useUrlOrgAccessGate } from "@/hooks/useUrlOrgAccessGate";
import { useRepoPermissionsSync } from "@/hooks/useRepoPermissionsSync";
import { useActiveGithubOrgForUserNonSuspense, useConnectedGithubOrgsNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { DashboardShellFallback } from "@/app/DashboardShellFallback";
import { DashboardRouterProvider, useDashboardRouter } from "@/hooks/useDashboardRouter";
import { OrgUrlSyncProvider } from "@/components/OrgUrlSyncProvider";
import { SidebarLayout } from "@/components/SidebarLayout";
import { useCodesmithSurface } from "@/lib/codesmithSurface";
import { githubLogin } from "@/lib/github-login";

// The Chrome extension iframe (`?embed=extension`) renders the page bare, as
// `_app.tsx` does: no rail, sidebars, command palette, shortcuts modal, or
// the shell's prefetch hooks. The page still needs a Suspense boundary below
// AppProviders: pages that suspend at their root (the PR resolver reads
// useActiveGithubOrgForUser there) would otherwise bubble to the layout
// boundary, discarding the query client and auth tree and re-suspending on
// the fresh cache.
function DashboardShell(t0) {
  const $ = _c(15);
  const {
    children
  } = t0;
  const router = useDashboardRouter();
  const {
    user,
    logout
  } = useAuth();
  const orgAccess = useUrlOrgAccessGate();
  const {
    activeNamedOrg
  } = useActiveGithubOrgForUserNonSuspense();
  const {
    status: orgsStatus
  } = useConnectedGithubOrgsNonSuspense();
  useSectionTiming("orgs.listing", orgsStatus);
  let t1;
  if ($[0] !== activeNamedOrg || $[1] !== router.pathname || $[2] !== router.query.org) {
    t1 = typeof router.query.org === "string" && !router.pathname.startsWith("/admin/internal-monitors") ? activeNamedOrg : null;
    $[0] = activeNamedOrg;
    $[1] = router.pathname;
    $[2] = router.query.org;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const urlOrg = t1;
  useRepoPermissionsSync(orgAccess.state === "allowed" ? urlOrg : null);
  const surface = useCodesmithSurface(router.query.embed);
  if (orgAccess.state === "pending") {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <DashboardShellFallback />;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (orgAccess.state === "denied") {
    const t2 = orgAccess.org;
    const t3 = orgAccess.fallbackOrg;
    let t4;
    if ($[5] !== user) {
      t4 = githubLogin(user);
      $[5] = user;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== logout || $[8] !== orgAccess.fallbackOrg || $[9] !== orgAccess.org || $[10] !== t4) {
      t5 = <OrgAccessSplash org={t2} fallbackOrg={t3} username={t4} onLogout={logout} />;
      $[7] = logout;
      $[8] = orgAccess.fallbackOrg;
      $[9] = orgAccess.org;
      $[10] = t4;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    return t5;
  }
  const content = children;
  let t2;
  if ($[12] !== content || $[13] !== surface) {
    t2 = <OrgUrlSyncProvider>{surface === "extension" ? <Suspense fallback={<DashboardShellFallback />}>{content}</Suspense> : <SidebarLayout>{content}</SidebarLayout>}</OrgUrlSyncProvider>;
    $[12] = content;
    $[13] = surface;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}
export default function DashboardLayout(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DashboardShellFallback />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <Suspense fallback={t1}><DashboardRouterProvider><AppProviders><DashboardShell>{children}</DashboardShell></AppProviders></DashboardRouterProvider></Suspense>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}